import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import JobCard from '../../components/cutlist/JobCard';
import { getAllJobs, deleteJob, updateJobSupplierStatus } from '../../services/cutlist-jobs';
import { generateGlassOrderRangePDF } from '../../pdf/glassOrderRangePDF';
import { generateFlexScreenOrderRangePDF } from '../../pdf/flexScreenOrderRangePDF';
import { exportFlexScreenOrderAsCSV } from '../../pdf/flexScreenOrderRangeCSV';
import { generateGlassOrderPDF } from '../../pdf/glassOrderPDF';
import { calculateWindow } from '../../calculations/index';
import { buildGlassSummary, verifyGlassPDF } from '../../calculations/orderVerify';
import { useConfirm } from '../../components/ConfirmDialog';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import {
  LayoutGrid, FileStack, Search as SearchIcon, Trash2, Loader2,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import { noAutofill } from '../../utils/noAutofill';

// ── Palette — same warm, coherent family as the Estimates pages.
const INK    = '#2B2620';
const WALNUT    = '#8B4513';
const WALNUT_LT = '#A0522D';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const ARCHIVE_DAYS = 90;  // 3 months — date fallback for archiving (see ARCHIVE RULE)

function sortJobs(jobs) {
  return [...jobs].sort((a, b) => {
    const aTime = (a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || a.orderDate || 0)).getTime();
    const bTime = (b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || b.orderDate || 0)).getTime();
    return bTime - aTime;
  });
}

function resolveJobDate(j) {
  const raw = j.createdAt ?? j.orderDate ?? j.updatedAt;
  if (!raw) return null;
  if (raw?.toDate) return raw.toDate();
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function isActiveJob(j) {
  // ── ARCHIVE RULE ──────────────────────────────────────────────────────────
  // A job is "current" (active) unless EITHER:
  //   (1) its status is 'installed' (job is done), OR
  //   (2) it's older than ARCHIVE_DAYS (date fallback so nothing lingers).
  // Either condition archives it. Grep: ARCHIVE RULE
  if ((j.status || '').toLowerCase() === 'installed') return false;
  const d = resolveJobDate(j);
  if (!d) return true;
  return (Date.now() - d.getTime()) < ARCHIVE_DAYS * 24 * 60 * 60 * 1000;
}

function daysAgo(j) {
  const d = resolveJobDate(j);
  if (!d) return null;
  return Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
}

// ── Week grouping ─────────────────────────────────────────────────────────────
function getWeekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function formatWeekRange(weekStart) {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  const startStr = weekStart.toLocaleDateString('en-US', opts);
  const endStr   = end.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

function getWeekLabel(weekStart, now) {
  const thisWeekStart = getWeekStart(now);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  if (weekStart.getTime() === thisWeekStart.getTime()) return 'This Week';
  if (weekStart.getTime() === lastWeekStart.getTime()) return 'Last Week';
  return 'Week of';
}

function groupJobsByWeek(jobs) {
  const groups = [];
  const map    = new Map();
  const now    = new Date();

  jobs.forEach(j => {
    const d = resolveJobDate(j) || new Date(0);
    const ws  = getWeekStart(d);
    const key = ws.toISOString().slice(0, 10);
    if (!map.has(key)) {
      map.set(key, { weekStart: ws, jobs: [] });
      groups.push(map.get(key));
    }
    map.get(key).jobs.push(j);
  });

  groups.sort((a, b) => b.weekStart - a.weekStart);
  return groups;
}

// ═══════════════════════════════════════════════════════════════════════════════
// REPAIR / WALK-IN GROUP STRUCTURE
// ═══════════════════════════════════════════════════════════════════════════════
// Each group has: { id, customerName, woNumber, rows: [...] }
// Rows inside glass groups and screen groups keep their own shape.
// Label auto-resolves to `{woNumber}-{rowIndex+1}` unless user types an override.
// ═══════════════════════════════════════════════════════════════════════════════

function newGlassRow() {
  return {
    id:           Date.now() + Math.random(),
    label:        '',                  // manual override; blank = auto-generated
    qty:          1,
    width:        '',
    height:       '',
    glassType:    'DLA',
    glassConfig:  'None',
    oaThick:      '7/8"',
    grids:        false,
    gridPattern:  '',
    gridQtyV:     '',
    gridQtyH:     '',
    gridColor:    '',
    defect:       'None',   // DEFECTIVE ON ARRIVAL — 'None' = normal walk-in
    defectOther:  '',       // free text when defect === 'Other'
  };
}

function newScreenRow() {
  return {
    id: Date.now() + Math.random(),
    label: '', qty: 1, width: '', height: '',
    meshType: 'STD Mesh',
    screenType: 'Flex Scrn',
    screenSize: 'Full Scrn',
    winType: '',
  };
}

function newGlassGroup() {
  return {
    id:           Date.now() + Math.random(),
    customerName: '',
    woNumber:     '',
    rows:         [newGlassRow()],
  };
}

function newScreenGroup() {
  return {
    id:           Date.now() + Math.random(),
    customerName: '',
    woNumber:     '',
    rows:         [newScreenRow()],
  };
}

// Given a group + row index, return the effective label for that row.
// If the user typed something in the row's label field, that wins.
// Otherwise auto-generate `{WO}-{index+1}`, or just `{index+1}` if no WO.
function resolvedLabel(group, rowIndex) {
  const row = group.rows[rowIndex];
  if (row.label && row.label.trim()) return row.label.trim();
  const wo = (group.woNumber || '').trim();
  return wo ? `${wo}-${rowIndex + 1}` : `${rowIndex + 1}`;
}

// ── Quick Glass Order row ─────────────────────────────────────────────────────
const QUICK_GLASS_WINDOW_TYPES = [
  { code: 'DH',   label: 'Double Hung' },
  { code: 'HS',   label: 'Horizontal Slider' },
  { code: 'CSMT', label: 'Casement' },
  { code: 'PW',   label: 'Picture Window' },
  { code: 'BSMT', label: 'Basement Hopper' },
  { code: '3HS',  label: '3-Lite Slider' },
  { code: 'BM',   label: 'Brickmould' },
];

const QUICK_GLASS_PKGS = ['DC', 'DCA', 'DLA', 'TLA', 'TLK'];
const QUICK_GLASS_OPTS = ['None', 'Obscure', 'Gray Tint', 'Bronze Tint', 'Tempered'];

function newQuickGlassRow() {
  return {
    id:          Date.now() + Math.random(),
    windowType:  'DH',
    qty:         1,
    width:       '',
    height:      '',
    glassPkg:    'DLA',
    glassOpt:    'None',
  };
}

// ── Shared input styles ───────────────────────────────────────────────────────
const INP = {
  padding: '5px 7px', borderRadius: '6px',
  border: '1px solid rgba(139,69,19,0.2)', fontSize: '11px',
  fontFamily: 'inherit', outline: 'none', background: '#faf8f5',
  color: '#1a1a1a', width: '100%', boxSizing: 'border-box',
};
const SEL = { ...INP, cursor: 'pointer' };

function FL({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <div style={{ fontSize: '9px', fontWeight: '700', color: '#9a7a60', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{label}</div>
      {children}
    </div>
  );
}

function JobRow({ job, dimmed, deletingId, onDelete }) {
  const age = daysAgo(job);
  return (
    <div style={{ position: 'relative', opacity: dimmed ? 0.58 : 1, transition: 'opacity 0.2s', filter: dimmed ? 'saturate(0.5)' : 'none' }}>
      <JobCard job={job} />
      {dimmed && age !== null && (
        <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.85)', fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px', letterSpacing: '0.04em', pointerEvents: 'none' }}>{age}d ago</div>
      )}
      <button onClick={e => onDelete(e, job.id)} disabled={deletingId === job.id} title="Delete job"
        style={{ position: 'absolute', top: '10px', right: '10px', width: '28px', height: '28px', borderRadius: '50%', border: `1px solid ${RUST}33`, background: PAPER, color: RUST, cursor: deletingId === job.id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', opacity: deletingId === job.id ? 0.5 : 1, transition: 'all 0.15s', zIndex: 10, fontFamily: 'inherit' }}
        onMouseEnter={e => { if (deletingId !== job.id) { e.currentTarget.style.background = `${RUST}14`; e.currentTarget.style.transform = 'scale(1.1)'; }}}
        onMouseLeave={e => { e.currentTarget.style.background = PAPER; e.currentTarget.style.transform = 'scale(1)'; }}
      >{deletingId === job.id ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Trash2 size={14} strokeWidth={2} />}</button>
    </div>
  );
}

function SectionLabel({ color, title, sub, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, boxShadow: `0 0 0 3px ${color}33` }} />
      <span style={{ fontSize: '12px', fontWeight: '800', color: '#2a2a2a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</span>
      {sub && <span style={{ fontSize: '11px', color: '#bbb', fontWeight: '500' }}>{sub}</span>}
      {count != null && <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#aaa' }}>{count} job{count !== 1 ? 's' : ''}</span>}
    </div>
  );
}

// ── Group header (shared by glass + screen) ──────────────────────────────────
function GroupHeader({ group, onUpdate, onRemoveGroup, canRemove, rowCount }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 32px', gap: '10px', alignItems: 'end', marginBottom: '10px' }}>
      <FL label="Customer Name">
        <input
          {...noAutofill}
          value={group.customerName}
          onChange={e => onUpdate('customerName', e.target.value)}
          placeholder="e.g. Noah Kwizera"
          style={{
            ...INP,
            fontWeight: group.customerName ? '700' : '400',
            color: group.customerName ? '#8B4513' : '#1a1a1a',
            fontSize: '13px',
            padding: '7px 10px',
          }}
        />
      </FL>
      <FL label="WO# / Contract">
        <input
          {...noAutofill}
          value={group.woNumber}
          onChange={e => onUpdate('woNumber', e.target.value)}
          placeholder="e.g. 8899"
          style={{
            ...INP,
            fontWeight: group.woNumber ? '700' : '400',
            color: group.woNumber ? '#1437a0' : '#1a1a1a',
            fontSize: '13px',
            padding: '7px 10px',
          }}
        />
      </FL>
      <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
        <button
          onClick={onRemoveGroup}
          disabled={!canRemove}
          title={canRemove ? 'Delete this customer group' : 'Cannot delete the last group'}
          style={{
            width: '30px', height: '30px', borderRadius: '50%',
            border: '1px solid rgba(220,50,50,0.25)',
            background: canRemove ? 'rgba(220,50,50,0.08)' : 'rgba(0,0,0,0.03)',
            color: canRemove ? '#cc3333' : '#ccc',
            fontSize: '14px',
            cursor: canRemove ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'inherit', flexShrink: 0,
          }}
        >×</button>
      </div>
    </div>
  );
}

// ── Glass Repair Row ──────────────────────────────────────────────────────────
function GlassRepairRow({ row, rowIndex, autoLabel, onUpdate, onRemove }) {
  const isTriple   = ['TLA','TDLA','TLK','TDC','TDCA'].includes(row.glassType);
  const isSingle   = row.glassType === 'SG';
  const isNonStdOA = row.oaThick !== '7/8"';
  const labelShown = row.label || autoLabel;

  const REPAIR_GLASS_PKGS = ['DLA', 'DCA', 'TLA', 'TDLA', 'TDCA', 'TLK', 'DC', 'TDC', 'SG'];
  const REPAIR_GLASS_OPTS = ['None', 'Obscure', 'Gray Tint', 'Bronze Tint', 'Tempered'];
  const REPAIR_OA_THICKS  = ['7/8"', '11/16"', '9/16"', '1"', '5/8"', '3/4"'];
  // DEFECTIVE ON ARRIVAL — reasons a glass piece needs supplier replacement
  const REPAIR_DEFECTS = ['None', 'Broken', 'Scratched', 'Cracked', 'Chipped',
    'Seal Failure', 'Wrong Grid Color', 'Wrong Grid Count', 'Wrong Size',
    'Wrong Glass Type', 'Wrong Tint', 'Other'];
  const isDefective = row.defect && row.defect !== 'None';

  return (
    <div style={{ background: 'white', border: '1px solid rgba(180,80,0,0.15)', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>

      {/* Row label strip — shows row number + auto-generated label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{
          width: '24px', height: '24px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #b85000, #d96a1a)',
          color: 'white', fontSize: '11px', fontWeight: '700',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>{rowIndex + 1}</div>
        <span style={{
          fontSize: '12px', fontWeight: '700', color: '#b85000',
          background: 'rgba(184,80,0,0.08)',
          padding: '3px 10px', borderRadius: '6px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}>
          {labelShown}
        </span>
        {!row.label && (
          <span style={{ fontSize: '10px', color: '#9a7a60', fontStyle: 'italic' }}>auto-generated</span>
        )}
      </div>

      {/* Label override + fields + remove */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 52px 72px 72px 90px 90px 90px 28px', gap: '8px', alignItems: 'end' }}>
        <FL label="Label Override (optional)">
          <input
            value={row.label}
            onChange={e => onUpdate('label', e.target.value)}
            placeholder={autoLabel}
            style={{ ...INP, fontStyle: row.label ? 'normal' : 'italic', color: row.label ? '#1a1a1a' : '#aaa' }}
          />
        </FL>
        <FL label="Qty">
          <input
            type="number"
            min="1"
            value={row.qty}
            onChange={e => {
              // Allow the field to be briefly empty while typing; store the
              // raw value so a typed "2" is never snapped back to 1.
              const v = e.target.value;
              onUpdate('qty', v === '' ? '' : (parseInt(v, 10) || ''));
            }}
            onBlur={e => {
              // On blur, settle to a valid quantity (minimum 1).
              const n = parseInt(e.target.value, 10);
              onUpdate('qty', (!n || n < 1) ? 1 : n);
            }}
            style={INP}
          />
        </FL>
        <FL label='Width "'>
          <input value={row.width} onChange={e => onUpdate('width', e.target.value)} placeholder='e.g. 24.5' style={INP} />
        </FL>
        <FL label='Height "'>
          <input value={row.height} onChange={e => onUpdate('height', e.target.value)} placeholder='e.g. 36.25' style={INP} />
        </FL>
        <FL label="Glass Config">
          <select value={row.glassType} onChange={e => onUpdate('glassType', e.target.value)} style={{ ...SEL, fontWeight: '700', color: isSingle ? '#cc3300' : isTriple ? '#1437a0' : '#1a1a1a' }}>
            {REPAIR_GLASS_PKGS.map(c => <option key={c} value={c}>{c}{c === 'SG' ? ' — Single' : ''}</option>)}
          </select>
        </FL>
        <FL label="OA Thick">
          <select value={row.oaThick} onChange={e => onUpdate('oaThick', e.target.value)} style={{ ...SEL, fontWeight: isNonStdOA ? '700' : '400', color: isNonStdOA ? '#cc3300' : '#1a1a1a' }}>
            {REPAIR_OA_THICKS.map(t => <option key={t}>{t}</option>)}
          </select>
        </FL>
        <FL label="Options">
          <select value={row.glassConfig} onChange={e => onUpdate('glassConfig', e.target.value)} style={{ ...SEL, fontWeight: row.glassConfig && row.glassConfig !== 'None' ? '700' : '400', color: row.glassConfig && row.glassConfig !== 'None' ? '#156a2e' : '#1a1a1a' }}>
            {REPAIR_GLASS_OPTS.map(o => <option key={o}>{o}</option>)}
          </select>
        </FL>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
          <button onClick={() => onRemove()} style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'rgba(220,50,50,0.1)', color: '#cc3333', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', flexShrink: 0 }}>×</button>
        </div>
      </div>

      {/* Badges */}
      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: row.grids ? '#b85000' : '#9a7a60' }}>
          <input type="checkbox" checked={row.grids} onChange={e => onUpdate('grids', e.target.checked)} style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#b85000' }} />
          Has Grids?
        </label>
        {isSingle && <span style={{ fontSize: '10px', fontWeight: '700', color: '#cc3300', background: 'rgba(200,50,0,0.08)', border: '1px solid rgba(200,50,0,0.2)', padding: '1px 8px', borderRadius: '999px' }}>Single Pane</span>}
        {isTriple && <span style={{ fontSize: '10px', fontWeight: '700', color: '#1437a0', background: 'rgba(20,55,160,0.08)', border: '1px solid rgba(20,55,160,0.2)', padding: '1px 8px', borderRadius: '999px' }}>Triple Pane</span>}
        {isNonStdOA && <span style={{ fontSize: '10px', fontWeight: '700', color: '#cc3300', background: 'rgba(200,50,0,0.08)', border: '1px solid rgba(200,50,0,0.2)', padding: '1px 8px', borderRadius: '999px' }}>Non-standard OA: {row.oaThick}</span>}
        {row.glassConfig && row.glassConfig !== 'None' && <span style={{ fontSize: '10px', fontWeight: '700', color: '#156a2e', background: 'rgba(21,106,46,0.08)', border: '1px solid rgba(21,106,46,0.2)', padding: '1px 8px', borderRadius: '999px' }}>{row.glassConfig}</span>}
        {isDefective && <span style={{ fontSize: '10px', fontWeight: '800', color: '#fff', background: '#c0392b', padding: '2px 9px', borderRadius: '999px', letterSpacing: '0.02em' }}>DEFECTIVE ON ARRIVAL</span>}
      </div>

      {/* Defective on Arrival — flags glass the supplier must replace */}
      <div style={{ display: 'grid', gridTemplateColumns: isDefective && row.defect === 'Other' ? '200px 1fr' : '200px', gap: '8px', marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed rgba(180,80,0,0.2)' }}>
        <FL label="Defective on Arrival?">
          <select
            value={row.defect || 'None'}
            onChange={e => onUpdate('defect', e.target.value)}
            style={{ ...SEL, fontWeight: isDefective ? '700' : '400', color: isDefective ? '#c0392b' : '#1a1a1a' }}
          >
            {REPAIR_DEFECTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </FL>
        {isDefective && row.defect === 'Other' && (
          <FL label="Describe the defect">
            <input
              value={row.defectOther || ''}
              onChange={e => onUpdate('defectOther', e.target.value)}
              placeholder="Type the issue — e.g. delaminated, foggy, bad spacer..."
              style={{ ...INP, fontWeight: '600' }}
            />
          </FL>
        )}
      </div>

      {/* Grids */}
      {row.grids && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 60px 60px 1fr', gap: '8px', marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed rgba(180,80,0,0.2)' }}>
          <FL label="Grid Pattern"><input value={row.gridPattern} onChange={e => onUpdate('gridPattern', e.target.value)} placeholder="e.g. Colonial, Prairie..." style={INP} /></FL>
          <FL label="# Vert"><input type="number" min="0" value={row.gridQtyV} onChange={e => onUpdate('gridQtyV', e.target.value)} placeholder="0" style={INP} /></FL>
          <FL label="# Horiz"><input type="number" min="0" value={row.gridQtyH} onChange={e => onUpdate('gridQtyH', e.target.value)} placeholder="0" style={INP} /></FL>
          <FL label="Grid Color"><input value={row.gridColor} onChange={e => onUpdate('gridColor', e.target.value)} placeholder="e.g. White, Tan..." style={INP} /></FL>
        </div>
      )}
    </div>
  );
}

// ── Screen Repair Row ─────────────────────────────────────────────────────────
function ScreenRepairRow({ row, rowIndex, autoLabel, onUpdate, onRemove }) {
  const labelShown = row.label || autoLabel;

  return (
    <div style={{ background: 'white', border: '1px solid rgba(180,80,0,0.15)', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>

      {/* Row label strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{
          width: '24px', height: '24px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #b85000, #d96a1a)',
          color: 'white', fontSize: '11px', fontWeight: '700',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>{rowIndex + 1}</div>
        <span style={{
          fontSize: '12px', fontWeight: '700', color: '#b85000',
          background: 'rgba(184,80,0,0.08)',
          padding: '3px 10px', borderRadius: '6px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}>
          {labelShown}
        </span>
        {!row.label && (
          <span style={{ fontSize: '10px', color: '#9a7a60', fontStyle: 'italic' }}>auto-generated</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 52px 72px 72px 110px 100px 100px 1fr 28px', gap: '8px', alignItems: 'end' }}>
        <FL label="Label Override (optional)">
          <input
            value={row.label}
            onChange={e => onUpdate('label', e.target.value)}
            placeholder={autoLabel}
            style={{ ...INP, fontStyle: row.label ? 'normal' : 'italic', color: row.label ? '#1a1a1a' : '#aaa' }}
          />
        </FL>
        <FL label="Qty"><input type="number" min="1" value={row.qty} onChange={e => onUpdate('qty', e.target.value)} style={INP} /></FL>
        <FL label='Scr Width "'><input value={row.width} onChange={e => onUpdate('width', e.target.value)} placeholder='e.g. 28.75' style={INP} /></FL>
        <FL label='Scr Height "'><input value={row.height} onChange={e => onUpdate('height', e.target.value)} placeholder='e.g. 52.5' style={INP} /></FL>
        <FL label="Mesh Type">
          <select value={row.meshType} onChange={e => onUpdate('meshType', e.target.value)} style={SEL}>
            {['STD Mesh','Solar','Pet Screen','No-See-Um'].map(m => <option key={m}>{m}</option>)}
          </select>
        </FL>
        <FL label="Screen Type">
          <select value={row.screenType} onChange={e => onUpdate('screenType', e.target.value)} style={SEL}>
            {['Flex Scrn','Half Scrn','Full Scrn'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FL>
        <FL label="Screen Size">
          <select value={row.screenSize} onChange={e => onUpdate('screenSize', e.target.value)} style={SEL}>
            {['Full Scrn','Half Scrn'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FL>
        <FL label="Win Type"><input value={row.winType} onChange={e => onUpdate('winType', e.target.value)} placeholder="e.g. DH, Casement..." style={INP} /></FL>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '1px' }}>
          <button onClick={() => onRemove()} style={{ width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'rgba(220,50,50,0.1)', color: '#cc3333', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', flexShrink: 0 }}>×</button>
        </div>
      </div>
      <div style={{ marginTop: '6px', fontSize: '10px', color: '#9a7a60', fontStyle: 'italic' }}>
        Adaptor Color defaults to <strong>WHT</strong> for all repair/walk-in orders.
      </div>
    </div>
  );
}

// ── Quick Glass Order Row ─────────────────────────────────────────────────────
function QuickGlassRow({ row, index, onUpdate, onRemove, canRemove, calculatedPanes }) {
  const hasResult = calculatedPanes && calculatedPanes.length > 0;
  const hasError  = calculatedPanes && calculatedPanes[0]?.error;

  return (
    <div style={{ background: 'white', border: `1px solid ${hasError ? 'rgba(220,50,50,0.3)' : hasResult ? 'rgba(20,55,160,0.2)' : 'rgba(180,80,0,0.15)'}`, borderRadius: '10px', padding: '10px 12px', marginBottom: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'linear-gradient(135deg, #8B4513, #A0522D)', color: 'white', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{index + 1}</div>
        <span style={{ fontSize: '11px', fontWeight: '600', color: '#6b4c30' }}>Window {index + 1}</span>
        {canRemove && (
          <button onClick={onRemove} style={{ marginLeft: 'auto', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(220,50,50,0.1)', color: '#cc3333', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}>×</button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 80px 80px 80px 100px', gap: '8px', alignItems: 'end' }}>
        <FL label="Window Type">
          <select value={row.windowType} onChange={e => onUpdate('windowType', e.target.value)} style={{ ...SEL, fontWeight: '700', color: '#1437a0' }}>
            {QUICK_GLASS_WINDOW_TYPES.map(t => <option key={t.code} value={t.code}>{t.label} ({t.code})</option>)}
          </select>
        </FL>
        <FL label="Qty">
          <input type="number" min="1" value={row.qty} onChange={e => onUpdate('qty', parseInt(e.target.value) || 1)} style={INP} />
        </FL>
        <FL label='OA Width "'>
          <input value={row.width} onChange={e => onUpdate('width', e.target.value)} placeholder='e.g. 28 1/2' style={INP} />
        </FL>
        <FL label='OA Height "'>
          <input value={row.height} onChange={e => onUpdate('height', e.target.value)} placeholder='e.g. 52 3/4' style={INP} />
        </FL>
        <FL label="Glass Pkg">
          <select value={row.glassPkg} onChange={e => onUpdate('glassPkg', e.target.value)} style={{ ...SEL, fontWeight: '700', color: '#1437a0' }}>
            {QUICK_GLASS_PKGS.map(p => <option key={p}>{p}</option>)}
          </select>
        </FL>
        <FL label="Options">
          <select value={row.glassOpt} onChange={e => onUpdate('glassOpt', e.target.value)} style={SEL}>
            {QUICK_GLASS_OPTS.map(o => <option key={o}>{o}</option>)}
          </select>
        </FL>
      </div>
      {hasResult && !hasError && (
        <div style={{ marginTop: '10px', padding: '8px 10px', background: 'rgba(20,55,160,0.04)', border: '1px solid rgba(20,55,160,0.15)', borderRadius: '8px' }}>
          <div style={{ fontSize: '9px', fontWeight: '700', color: '#1437a0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Calculated Glass Panes</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {calculatedPanes.map((pane, pi) => (
              <div key={pi} style={{ fontSize: '10px', fontWeight: '600', color: '#1437a0', background: 'rgba(20,55,160,0.08)', border: '1px solid rgba(20,55,160,0.2)', padding: '3px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                {pane.label}: {pane.width}" × {pane.height}"
              </div>
            ))}
          </div>
        </div>
      )}
      {hasError && (
        <div style={{ marginTop: '8px', fontSize: '10px', color: '#cc3333', fontStyle: 'italic' }}>{calculatedPanes[0].error}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const navigate = useNavigate();

  const [jobs, setJobs]               = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const confirm = useConfirm();
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError]             = useState(null);
  const [deletingId, setDeletingId]   = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showArchived, setShowArchived] = useState(false);

  const [exportFrom,  setExportFrom]  = useState('');
  const [exportTo,    setExportTo]    = useState('');
  const [exporting,   setExporting]   = useState(null);
  const [exportCount, setExportCount] = useState(null);
  const [includeSent, setIncludeSent] = useState(false);
  // GLASS EXPORT CHECKPOINT — holds { summary, verify, blob, fileName } while
  // the confirm popup is open; null when closed.
  const [glassVerify, setGlassVerify] = useState(null);
  const [markingIds, setMarkingIds]   = useState({});

  // ── Grouped repair state ────────────────────────────────────────────────
  const [showRepairs,   setShowRepairs]   = useState(false);
  const [repairTab,     setRepairTab]     = useState('glass');
  const [glassGroups,   setGlassGroups]   = useState([newGlassGroup()]);
  const [screenGroups,  setScreenGroups]  = useState([newScreenGroup()]);

  // ── Quick Glass Order state ──────────────────────────────────────────────
  const [showQuickGlass,       setShowQuickGlass]       = useState(false);
  const [quickGlassRows,       setQuickGlassRows]       = useState([newQuickGlassRow()]);
  const [quickGlassCustomer,   setQuickGlassCustomer]   = useState('');
  const [quickGlassWO,         setQuickGlassWO]         = useState('');
  const [generatingQuickGlass, setGeneratingQuickGlass] = useState(false);
  const [quickGlassCalcPanes,  setQuickGlassCalcPanes]  = useState({});

  const WEEKS_PER_PAGE = 2;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadJobs();
    const t = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { applySearch(searchQuery); }, [jobs, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newCalc = {};
    quickGlassRows.forEach(row => {
      if (!row.width || !row.height) { newCalc[row.id] = []; return; }
      try {
        const winInput = {
          type:        row.windowType,
          width:       row.width,
          height:      row.height,
          qty:         row.qty,
          label:       quickGlassWO ? `${quickGlassWO}-${quickGlassRows.findIndex(r => r.id === row.id) + 1}` : `QG-${quickGlassRows.findIndex(r => r.id === row.id) + 1}`,
          KeeperGlassPkg:  row.glassPkg,
          KeeperGlassOpts: row.glassOpt === 'None' ? '' : row.glassOpt,
          LockGlassPkg:    row.glassPkg,
          LockGlassOpts:   row.glassOpt === 'None' ? '' : row.glassOpt,
          PictureGlassPkg:  row.glassPkg,
          PictureGlassOpts: row.glassOpt === 'None' ? '' : row.glassOpt,
          CasementGlassPkg:  row.glassPkg,
          CasementGlassOpts: row.glassOpt === 'None' ? '' : row.glassOpt,
          HopperGlassPkg:  row.glassPkg,
          HopperGlassOpts: row.glassOpt === 'None' ? '' : row.glassOpt,
          finType:     'Without Fin',
          config:      'Single',
          color:       'White',
          screenType:  'None',
          gasFill:     'Argon',
          mullCount:   0,
          nightLatches: false,
          kd:          false,
          insulatedFrame: false,
        };
        const result = calculateWindow(winInput);
        const panes = (result.glass || []).map(g => ({
          label:  g.label || '',
          width:  typeof g.width  === 'number' ? g.width.toFixed(4).replace(/\.?0+$/, '') : String(g.width  || ''),
          height: typeof g.height === 'number' ? g.height.toFixed(4).replace(/\.?0+$/, '') : String(g.height || ''),
        }));
        newCalc[row.id] = panes;
      } catch(e) {
        newCalc[row.id] = [{ error: `Cannot calculate: ${e.message}` }];
      }
    });
    setQuickGlassCalcPanes(newCalc);
  }, [quickGlassRows, quickGlassWO]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadJobs() {
    setLoading(true); setError(null);
    try {
      const data   = await getAllJobs();
      const sorted = sortJobs(data);
      setJobs(sorted); setFiltered(sorted);
    } catch (e) { console.error(e); setError('Failed to load jobs. Check your connection.'); }
    finally { setLoading(false); }
  }

  function applySearch(query) {
    if (!query.trim()) { setFiltered(jobs); return; }
    const lower = query.toLowerCase();
    setFiltered(jobs.filter(j =>
      (j.customerName || '').toLowerCase().includes(lower) ||
      (j.woNumber     || '').toLowerCase().includes(lower) ||
      (j.city         || '').toLowerCase().includes(lower) ||
      (j.jobName      || '').toLowerCase().includes(lower)
    ));
    setCurrentPage(1);
  }

  async function handleDelete(e, jobId) {
    e.stopPropagation();
    if (!(await confirm({ title: 'Delete this job?', message: 'This cannot be undone.', tone: 'danger', confirmText: 'Delete' }))) return;
    setDeletingId(jobId);
    try {
      await deleteJob(jobId);
      setJobs(prev => sortJobs(prev.filter(j => j.id !== jobId)));
    } catch (err) { console.error(err); alert('Failed to delete job. Try again.'); }
    finally { setDeletingId(null); }
  }

  async function markSupplierSent(jobId, field, value) {
    setMarkingIds(prev => ({ ...prev, [jobId + field]: true }));
    try {
      await updateJobSupplierStatus(jobId, field, value);
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, [field]: value } : j));
    } catch(e) { console.error(e); alert('Failed to update. Try again.'); }
    finally { setMarkingIds(prev => { const n = {...prev}; delete n[jobId + field]; return n; }); }
  }

  const greeting = () => {
    const h = currentTime.getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formattedDate = currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const recentJobs    = jobs.filter(j => {
    const d = resolveJobDate(j);
    return d && (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
  }).length;

  function getJobsInRange(supplierKey) {
    if (!exportFrom || !exportTo) return [];
    const from = new Date(exportFrom + 'T00:00:00');
    const to   = new Date(exportTo   + 'T23:59:59');
    const inRange = jobs.filter(j => { const d = resolveJobDate(j); return d && d >= from && d <= to; });
    if (includeSent) return inRange;
    return inRange.filter(j => !j[supplierKey]);
  }

  function handleDateChange(field, val) {
    const from = field === 'from' ? val : exportFrom;
    const to   = field === 'to'   ? val : exportTo;
    if (field === 'from') setExportFrom(val); else setExportTo(val);
    if (from && to) {
      const fromD = new Date(from + 'T00:00:00'), toD = new Date(to + 'T23:59:59');
      const inRange = jobs.filter(j => { const d = resolveJobDate(j); return d && d >= fromD && d <= toD; });
      let glassCount = 0, screenCount = 0;
      inRange.forEach(j => { (j.calculatedWindows || []).forEach(w => { glassCount += (w.glass || []).length; if (w.flexScreen) screenCount++; }); });
      setExportCount({ jobs: inRange.length, glass: glassCount, screens: screenCount });
    } else { setExportCount(null); }
  }

  // ── Group mutators ──────────────────────────────────────────────────────────
  function updateGlassGroup(groupId, field, val) {
    setGlassGroups(prev => prev.map(g => g.id === groupId ? { ...g, [field]: val } : g));
  }
  function updateGlassRow(groupId, rowId, field, val) {
    setGlassGroups(prev => prev.map(g => g.id !== groupId ? g : {
      ...g,
      rows: g.rows.map(r => r.id === rowId ? { ...r, [field]: val } : r),
    }));
  }
  function addGlassRow(groupId) {
    setGlassGroups(prev => prev.map(g => g.id !== groupId ? g : { ...g, rows: [...g.rows, newGlassRow()] }));
  }
  function removeGlassRow(groupId, rowId) {
    setGlassGroups(prev => prev.map(g => g.id !== groupId ? g : { ...g, rows: g.rows.filter(r => r.id !== rowId) }));
  }
  function addGlassGroup() {
    setGlassGroups(prev => [...prev, newGlassGroup()]);
  }
  function removeGlassGroup(groupId) {
    setGlassGroups(prev => prev.length <= 1 ? prev : prev.filter(g => g.id !== groupId));
  }

  function updateScreenGroup(groupId, field, val) {
    setScreenGroups(prev => prev.map(g => g.id === groupId ? { ...g, [field]: val } : g));
  }
  function updateScreenRow(groupId, rowId, field, val) {
    setScreenGroups(prev => prev.map(g => g.id !== groupId ? g : {
      ...g,
      rows: g.rows.map(r => r.id === rowId ? { ...r, [field]: val } : r),
    }));
  }
  function addScreenRow(groupId) {
    setScreenGroups(prev => prev.map(g => g.id !== groupId ? g : { ...g, rows: [...g.rows, newScreenRow()] }));
  }
  function removeScreenRow(groupId, rowId) {
    setScreenGroups(prev => prev.map(g => g.id !== groupId ? g : { ...g, rows: g.rows.filter(r => r.id !== rowId) }));
  }
  function addScreenGroup() {
    setScreenGroups(prev => [...prev, newScreenGroup()]);
  }
  function removeScreenGroup(groupId) {
    setScreenGroups(prev => prev.length <= 1 ? prev : prev.filter(g => g.id !== groupId));
  }

  // ── Flatten groups to flat arrays for PDF/CSV consumption ──────────────────
  // Each row gets customerName + woNumber + resolved label attached so the
  // PDFs can continue grouping by customerName exactly as they do today.
  function flattenGlassGroups() {
    const out = [];
    glassGroups.forEach(group => {
      group.rows.forEach((row, idx) => {
        if (!row.width || !row.height) return;
        out.push({
          ...row,
          customerName: group.customerName || '',
          woNumber:     group.woNumber || '',
          label:        resolvedLabel(group, idx),
        });
      });
    });
    return out;
  }
  function flattenScreenGroups() {
    const out = [];
    screenGroups.forEach(group => {
      group.rows.forEach((row, idx) => {
        if (!row.width || !row.height) return;
        out.push({
          ...row,
          customerName: group.customerName || '',
          woNumber:     group.woNumber || '',
          label:        resolvedLabel(group, idx),
        });
      });
    });
    return out;
  }

  const validGlassCount  = flattenGlassGroups().length;
  const validScreenCount = flattenScreenGroups().length;
  const totalRepairBadge = validGlassCount + validScreenCount;

  // ── Quick Glass Order handlers ─────────────────────────────────────────────
  const updateQuickGlassRow = (id, field, val) =>
    setQuickGlassRows(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));

  const validQuickRows = quickGlassRows.filter(r => r.width && r.height && !(quickGlassCalcPanes[r.id]?.[0]?.error));

  async function handleGenerateQuickGlass() {
    if (!validQuickRows.length) { alert('Enter at least one window with OA width and height.'); return; }
    setGeneratingQuickGlass(true);
    try {
      const calculatedWindows = [];
      quickGlassRows.forEach((row, idx) => {
        if (!row.width || !row.height) return;
        try {
          const winInput = {
            type:        row.windowType,
            width:       row.width,
            height:      row.height,
            qty:         row.qty,
            label:       quickGlassWO ? `${quickGlassWO}-${idx + 1}` : `QG-${idx + 1}`,
            KeeperGlassPkg:   row.glassPkg,
            KeeperGlassOpts:  row.glassOpt === 'None' ? '' : row.glassOpt,
            LockGlassPkg:     row.glassPkg,
            LockGlassOpts:    row.glassOpt === 'None' ? '' : row.glassOpt,
            PictureGlassPkg:  row.glassPkg,
            PictureGlassOpts: row.glassOpt === 'None' ? '' : row.glassOpt,
            CasementGlassPkg:  row.glassPkg,
            CasementGlassOpts: row.glassOpt === 'None' ? '' : row.glassOpt,
            HopperGlassPkg:   row.glassPkg,
            HopperGlassOpts:  row.glassOpt === 'None' ? '' : row.glassOpt,
            finType:      'Without Fin',
            config:       'Single',
            color:        'White',
            screenType:   'None',
            gasFill:      'Argon',
            mullCount:    0,
            nightLatches: false,
            kd:           false,
            insulatedFrame: false,
          };
          const result = calculateWindow(winInput);
          calculatedWindows.push(result);
        } catch(e) {
          console.warn(`Skipping row ${idx + 1}: ${e.message}`);
        }
      });

      if (!calculatedWindows.length) {
        alert('Could not calculate glass for any of the entered windows. Check your inputs.');
        return;
      }

      const job = {
        customerName: quickGlassCustomer || 'Quick Glass Order',
        woNumber:     quickGlassWO || 'REPAIR',
        orderDate:    new Date().toISOString().slice(0, 10),
      };

      await generateGlassOrderPDF(job, calculatedWindows, false);
    } catch(e) {
      console.error('Quick glass order failed:', e);
      alert('Failed to generate PDF. Check console for details.');
    } finally {
      setGeneratingQuickGlass(false);
    }
  }

  async function handleExportGlass() {
    const rangeJobs = getJobsInRange('glassSent');
    const repairs   = flattenGlassGroups();
    if (!rangeJobs.length && !repairs.length) { alert('No jobs or repair items found.'); return; }
    setExporting('glass');
    try {
      const fmt = d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      // Build the PDF in memory + get its row data — do NOT download yet.
      const result = await generateGlassOrderRangePDF(
        rangeJobs, fmt(exportFrom), fmt(exportTo), false, repairs, true
      );
      if (!result || !result.blob) { alert('Failed to generate PDF.'); return; }
      // Verify the ACTUAL printed rows against the database expectation.
      const summary = buildGlassSummary(rangeJobs);
      const verify  = verifyGlassPDF(result.groups || [], rangeJobs);
      const fileName = `TWP_GlassOrder_${(exportFrom||'').replace(/[^0-9]/g,'-')}_to_${(exportTo||'').replace(/[^0-9]/g,'-')}.pdf`;
      // Open the confirm popup — the blob is held until the user confirms.
      setGlassVerify({ summary, verify, blob: result.blob, fileName });
    } catch(e) { console.error(e); alert('Failed to generate PDF.'); }
    finally { setExporting(null); }
  }

  // Called from the popup's "Confirm & Export" button — downloads the held PDF.
  function confirmGlassExport() {
    if (!glassVerify || !glassVerify.blob) { setGlassVerify(null); return; }
    const url = URL.createObjectURL(glassVerify.blob);
    const a = document.createElement('a');
    a.href = url; a.download = glassVerify.fileName || 'TWP_GlassOrder.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    setGlassVerify(null);
  }

  async function handleExportScreens() {
    const rangeJobs = getJobsInRange('flexSent');
    const repairs   = flattenScreenGroups();
    if (!rangeJobs.length && !repairs.length) { alert('No jobs or repair items found.'); return; }
    setExporting('screen');
    try {
      const fmt = d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      exportFlexScreenOrderAsCSV(rangeJobs, fmt(exportFrom), fmt(exportTo), repairs);
    } catch(e) { console.error(e); alert('Failed to generate PDF.'); }
    finally { setExporting(null); }
  }

  const breadcrumb = 'Dashboard › Cutlist Dashboard';

  return (
    <AppLayout
      title="Cutlist Dashboard"
      breadcrumb={breadcrumb}
      topBarActions={
        <Button size="small" onClick={() => navigate('/cutlist/new')}>+ New Job</Button>
      }
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ maxWidth: '1440px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>

        {/* ── GREETING CARD ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', padding: '20px 28px', background: PAPER, borderRadius: '16px', border: '1px solid rgba(139,69,19,0.08)', boxShadow: '0 2px 16px rgba(139,69,19,0.06)', fontFamily: FONT_SANS }}>
          <div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: 600, color: INK, marginBottom: '3px' }}>{greeting()}</div>
            <div style={{ fontSize: '13px', color: STONE }}>{formattedDate}</div>
          </div>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <div style={{ textAlign: 'center', padding: '10px 18px', borderRadius: '12px', background: '#EAF1EA', border: '1px solid #cfe3d0' }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: MOSS, lineHeight: 1 }}>{loading ? '—' : jobs.filter(j => isActiveJob(j)).length}</div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: STONE, marginTop: '3px' }}>Current</div>
            </div>
            {recentJobs > 0 && (
              <div style={{ textAlign: 'center', padding: '10px 18px', borderRadius: '12px', background: 'rgba(139,69,19,0.05)', border: '1px solid rgba(139,69,19,0.1)' }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: WALNUT, lineHeight: 1 }}>{recentJobs}</div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: STONE, marginTop: '3px' }}>This Week</div>
              </div>
            )}
            <div style={{ textAlign: 'center', padding: '10px 18px', borderRadius: '12px', background: '#F5F3F0', border: '1px solid #e8e2da' }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: STONE, lineHeight: 1 }}>{loading ? '—' : jobs.filter(j => !isActiveJob(j)).length}</div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: STONE, marginTop: '3px' }}>Past</div>
            </div>
            <Button size="small" onClick={() => navigate('/cutlist/new')}>+ Create New Job</Button>
          </div>
        </div>

        {/* ── SEARCH ────────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: '20px', position: 'relative' }}>
          <SearchIcon size={16} strokeWidth={2} color={STONE} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
          <input type="text" name="twp-cutlist-search" autoComplete="off" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by customer name, WO#, city, or job name..."
            style={{ width: '100%', padding: '13px 44px 13px 46px', borderRadius: '12px', border: '1px solid rgba(139,69,19,0.15)', background: PAPER, fontSize: '14px', color: INK, fontFamily: FONT_SANS, outline: 'none', boxSizing: 'border-box', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'border-color 0.15s, box-shadow 0.15s' }}
            onFocus={e => { e.target.style.borderColor = WALNUT; e.target.style.boxShadow = `0 0 0 3px rgba(139,69,19,0.1)`; }}
            onBlur={e => { e.target.style.borderColor = 'rgba(139,69,19,0.15)'; e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(139,69,19,0.08)', border: 'none', width: '24px', height: '24px', borderRadius: '50%', color: WALNUT, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', lineHeight: 1 }}>×</button>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════════════
            QUICK GLASS ORDER PANEL
        ══════════════════════════════════════════════════════════════════════ */}
        <div style={{ background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.15)', boxShadow: '0 2px 12px rgba(139,69,19,0.07)', marginBottom: '20px', fontFamily: FONT_SANS }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: WALNUT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <LayoutGrid size={18} strokeWidth={2} color="white" />
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: INK }}>Quick Glass Order</div>
                <div style={{ fontSize: '11px', color: STONE }}>Enter window OA sizes — glass dimensions are calculated automatically per window type</div>
              </div>
            </div>
            <button
              onClick={() => setShowQuickGlass(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 20px', borderRadius: '8px', border: '1px solid rgba(139,69,19,0.25)', background: showQuickGlass ? 'rgba(139,69,19,0.08)' : PAPER, color: WALNUT, fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s' }}
            >
              {showQuickGlass ? <ChevronUp size={13} strokeWidth={2} /> : <ChevronDown size={13} strokeWidth={2} />}
              {showQuickGlass ? 'Close' : 'Open'}
            </button>
          </div>

          {showQuickGlass && (
            <div style={{ marginTop: '18px', borderTop: '1px solid rgba(139,69,19,0.08)', paddingTop: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <FL label="Customer Name (optional)">
                  <input name="twp-quickglass-customer" autoComplete="off" value={quickGlassCustomer} onChange={e => setQuickGlassCustomer(e.target.value)} placeholder="e.g. John Smith" style={{ ...INP, fontSize: '13px', padding: '8px 12px' }} />
                </FL>
                <FL label="WO / Contract # (optional — used for item codes on PDF)">
                  <input name="twp-quickglass-wo" autoComplete="off" value={quickGlassWO} onChange={e => setQuickGlassWO(e.target.value)} placeholder="e.g. 8834" style={{ ...INP, fontSize: '13px', padding: '8px 12px' }} />
                </FL>
              </div>
              <div style={{ marginBottom: '14px', padding: '10px 14px', background: 'rgba(139,69,19,0.04)', border: '1px solid rgba(139,69,19,0.12)', borderRadius: '8px', fontSize: '11px', color: WALNUT, lineHeight: '1.6' }}>
                <strong>How it works:</strong> Enter the window OA (overall) width and height — the same measurements you would take off the old window frame.
                Select the window type and glass package. The system calculates the correct glass pane sizes automatically
                (e.g. a Double Hung gives you Keeper and Lock panes, a 3-Lite Slider gives you the correct pane breakdown)
                and generates a glass order PDF ready to send to Tracy Corners.
              </div>
              {quickGlassRows.map((row, idx) => (
                <QuickGlassRow
                  key={row.id}
                  row={row}
                  index={idx}
                  onUpdate={(field, val) => updateQuickGlassRow(row.id, field, val)}
                  onRemove={() => setQuickGlassRows(prev => prev.filter(r => r.id !== row.id))}
                  canRemove={quickGlassRows.length > 1}
                  calculatedPanes={quickGlassCalcPanes[row.id] || []}
                />
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setQuickGlassRows(prev => [...prev, newQuickGlassRow()])}
                  style={{ padding: '7px 16px', borderRadius: '7px', border: '1px dashed rgba(139,69,19,0.4)', background: 'transparent', color: WALNUT, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS }}
                >+ Add Another Window</button>
                {quickGlassRows.length > 1 && (
                  <button
                    onClick={() => setQuickGlassRows([newQuickGlassRow()])}
                    style={{ padding: '7px 16px', borderRadius: '7px', border: `1px solid ${RUST}33`, background: 'transparent', color: RUST, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS }}
                  >Clear All</button>
                )}
                <div style={{ flex: 1 }} />
                <div style={{ fontSize: '11px', color: STONE, fontStyle: 'italic' }}>
                  {validQuickRows.length} of {quickGlassRows.length} window{quickGlassRows.length !== 1 ? 's' : ''} ready
                </div>
                <button
                  onClick={handleGenerateQuickGlass}
                  disabled={generatingQuickGlass || validQuickRows.length === 0}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '11px 28px', borderRadius: '10px', border: 'none',
                    background: validQuickRows.length > 0 ? WALNUT : 'rgba(139,69,19,0.08)',
                    color: validQuickRows.length > 0 ? 'white' : '#ccc',
                    fontSize: '13px', fontWeight: '700',
                    cursor: generatingQuickGlass || validQuickRows.length === 0 ? 'not-allowed' : 'pointer',
                    fontFamily: FONT_SANS,
                    boxShadow: validQuickRows.length > 0 ? '0 4px 14px rgba(139,69,19,0.35)' : 'none',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { if (validQuickRows.length > 0 && !generatingQuickGlass) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(139,69,19,0.45)'; }}}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = validQuickRows.length > 0 ? '0 4px 14px rgba(139,69,19,0.35)' : 'none'; }}
                >
                  <LayoutGrid size={14} strokeWidth={2} /> {generatingQuickGlass ? 'Generating...' : 'Generate Glass Order PDF'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── ORDER EXPORT PANEL ────────────────────────────────────────────── */}
        <div style={{ background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '28px', fontFamily: FONT_SANS }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: WALNUT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FileStack size={16} strokeWidth={2} color="white" />
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: INK }}>Order Export</div>
              <div style={{ fontSize: '11px', color: STONE }}>Export glass & flex screen orders for a date range</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {['from','to'].map(field => (
              <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '10px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{field === 'from' ? 'From' : 'To'}</label>
                <input type="date" name={`twp-cutlist-export-${field}`} autoComplete="off" value={field === 'from' ? exportFrom : exportTo} min={field === 'to' ? exportFrom : undefined}
                  onChange={e => handleDateChange(field, e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(139,69,19,0.2)', fontSize: '13px', color: INK, fontFamily: FONT_SANS, outline: 'none', background: '#faf8f5', cursor: 'pointer' }}
                  onFocus={e => { e.target.style.borderColor = WALNUT; e.target.style.boxShadow = '0 0 0 3px rgba(139,69,19,0.1)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(139,69,19,0.2)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            ))}
            {exportCount && (
              <div style={{ marginTop: '16px', padding: '7px 12px', borderRadius: '8px', background: 'rgba(139,69,19,0.06)', border: '1px solid rgba(139,69,19,0.12)', fontSize: '11px', color: WALNUT, fontWeight: '600', whiteSpace: 'nowrap' }}>
                {exportCount.jobs} job{exportCount.jobs !== 1 ? 's' : ''}  -  {exportCount.glass} glass pane{exportCount.glass !== 1 ? 's' : ''}  -  {exportCount.screens} flex screen{exportCount.screens !== 1 ? 's' : ''}
              </div>
            )}
            {exportFrom && exportTo && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '16px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: includeSent ? OCHRE : STONE, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={includeSent} onChange={e => setIncludeSent(e.target.checked)} style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: OCHRE }} />
                Include already sent
              </label>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              {[
                { key: 'glass',  label: 'Export for Tracy Corners', fn: handleExportGlass,   color: WALNUT, shadow: 'rgba(139,69,19,0.3)' },
                { key: 'screen', label: 'Export for FlexScreen',    fn: handleExportScreens, color: MOSS,   shadow: 'rgba(59,107,68,0.3)' },
              ].map(({ key, label, fn, color, shadow }) => {
                const enabled = !!(exportFrom && exportTo);
                const busy    = exporting === key;
                return (
                  <button key={key} onClick={fn} disabled={!enabled || busy}
                    style={{ padding: '9px 16px', borderRadius: '9px', border: 'none', background: enabled ? color : 'rgba(139,69,19,0.08)', color: enabled ? 'white' : '#ccc', fontSize: '12px', fontWeight: '700', cursor: enabled ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: enabled ? `0 3px 10px ${shadow}` : 'none', transition: 'all 0.2s', fontFamily: FONT_SANS, whiteSpace: 'nowrap' }}
                    onMouseEnter={e => { if (enabled && !busy) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 5px 14px ${shadow}`; }}}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = enabled ? `0 3px 10px ${shadow}` : 'none'; }}
                  >{busy ? 'Generating...' : label}</button>
                );
              })}
            </div>
          </div>

          {/* Jobs in range — supplier status */}
          {exportFrom && exportTo && (() => {
            const from = new Date(exportFrom + 'T00:00:00');
            const to   = new Date(exportTo   + 'T23:59:59');
            const inRange = jobs.filter(j => { const d = resolveJobDate(j); return d && d >= from && d <= to; });
            if (!inRange.length) return null;
            return (
              <div style={{ marginTop: '14px', borderTop: '1px solid rgba(139,69,19,0.08)', paddingTop: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Jobs in range - supplier status</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {inRange.map(job => (
                    <div key={job.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '8px', background: 'rgba(139,69,19,0.03)', border: '1px solid rgba(139,69,19,0.08)' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: INK, minWidth: '140px' }}>
                        {job.customerName || '—'} <span style={{ color: STONE, fontWeight: '400' }}>WO#{job.woNumber}</span>
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ fontSize: '10px', color: STONE, fontWeight: '600' }}>Glass:</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '600', color: job.glassSent ? MOSS : STONE }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: job.glassSent ? MOSS : STONE, flexShrink: 0 }} />
                          {job.glassSent ? 'Sent' : 'Not sent'}
                        </span>
                        <button disabled={!!markingIds[job.id+'glassSent']} onClick={() => markSupplierSent(job.id, 'glassSent', !job.glassSent)} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontFamily: FONT_SANS, fontWeight: '600', background: job.glassSent ? 'rgba(161,61,45,0.08)' : 'rgba(59,107,68,0.1)', color: job.glassSent ? RUST : MOSS }}>
                          {markingIds[job.id+'glassSent'] ? '...' : job.glassSent ? 'Unmark' : 'Mark sent'}
                        </button>
                      </div>
                      <div style={{ width: '1px', height: '16px', background: 'rgba(139,69,19,0.1)' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ fontSize: '10px', color: STONE, fontWeight: '600' }}>Flex:</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '600', color: job.flexSent ? MOSS : STONE }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: job.flexSent ? MOSS : STONE, flexShrink: 0 }} />
                          {job.flexSent ? 'Sent' : 'Not sent'}
                        </span>
                        <button disabled={!!markingIds[job.id+'flexSent']} onClick={() => markSupplierSent(job.id, 'flexSent', !job.flexSent)} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontFamily: FONT_SANS, fontWeight: '600', background: job.flexSent ? 'rgba(161,61,45,0.08)' : 'rgba(59,107,68,0.1)', color: job.flexSent ? RUST : MOSS }}>
                          {markingIds[job.id+'flexSent'] ? '...' : job.flexSent ? 'Unmark' : 'Mark sent'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Repair / Walk-in panel */}
          {exportFrom && exportTo && (
            <div style={{ marginTop: '14px', borderTop: '1px solid rgba(139,69,19,0.08)', paddingTop: '12px' }}>
              <button onClick={() => setShowRepairs(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', background: showRepairs ? 'rgba(180,80,0,0.08)' : 'rgba(139,69,19,0.04)', border: `1px solid ${showRepairs ? 'rgba(180,80,0,0.3)' : 'rgba(139,69,19,0.15)'}`, borderRadius: '8px', padding: '7px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: showRepairs ? '#b85000' : '#7a4a1a', fontFamily: 'inherit', transition: 'all 0.2s' }}
              >
                {showRepairs ? 'v' : '>'} Add Repair / Walk-in Items
                {totalRepairBadge > 0 && (
                  <span style={{ background: '#b85000', color: 'white', fontSize: '10px', padding: '1px 7px', borderRadius: '999px', fontWeight: '800' }}>{totalRepairBadge}</span>
                )}
              </button>

              {showRepairs && (
                <div style={{ marginTop: '10px', background: 'rgba(255,242,224,0.5)', border: '1px solid rgba(180,80,0,0.18)', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ fontSize: '11px', color: '#b85000', fontWeight: '600', marginBottom: '12px' }}>
                    Repair / Walk-in Items — each <strong>customer group</strong> gets its own section in the PDF, just like regular jobs.
                    Enter the <strong>Customer Name</strong> and <strong>WO#</strong> once per group — rows auto-label as <code>WO#-1</code>, <code>WO#-2</code>, <code>WO#-3</code>…
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
                    {[{ id: 'glass', label: 'Glass Repairs' }, { id: 'screen', label: 'FlexScreen Repairs' }].map(tab => (
                      <button key={tab.id} onClick={() => setRepairTab(tab.id)}
                        style={{ padding: '6px 14px', borderRadius: '7px', border: 'none', background: repairTab === tab.id ? '#b85000' : 'rgba(180,80,0,0.1)', color: repairTab === tab.id ? 'white' : '#b85000', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
                      >
                        {tab.label}
                        {repairTab !== tab.id && tab.id === 'glass'  && validGlassCount  > 0 ? ` (${validGlassCount})`  : ''}
                        {repairTab !== tab.id && tab.id === 'screen' && validScreenCount > 0 ? ` (${validScreenCount})` : ''}
                      </button>
                    ))}
                  </div>

                  {/* ── GLASS TAB ─────────────────────────────────────────────── */}
                  {repairTab === 'glass' && (
                    <div>
                      {glassGroups.map((group, gIdx) => (
                        <div key={group.id} style={{
                          background: 'rgba(255,237,213,0.35)',
                          border: '1px solid rgba(180,80,0,0.25)',
                          borderRadius: '12px',
                          padding: '12px 14px',
                          marginBottom: '12px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <div style={{
                              fontSize: '10px', fontWeight: '800',
                              color: 'white', background: '#b85000',
                              padding: '3px 9px', borderRadius: '999px',
                              letterSpacing: '0.05em',
                            }}>CUSTOMER {gIdx + 1}</div>
                            <div style={{ fontSize: '10px', color: '#9a7a60', fontStyle: 'italic' }}>
                              {group.rows.filter(r => r.width && r.height).length} / {group.rows.length} row{group.rows.length !== 1 ? 's' : ''} filled
                            </div>
                          </div>
                          <GroupHeader
                            group={group}
                            onUpdate={(f, v) => updateGlassGroup(group.id, f, v)}
                            onRemoveGroup={() => removeGlassGroup(group.id)}
                            canRemove={glassGroups.length > 1}
                            rowCount={group.rows.length}
                          />
                          {group.rows.map((row, rIdx) => (
                            <GlassRepairRow
                              key={row.id}
                              row={row}
                              rowIndex={rIdx}
                              autoLabel={(() => {
                                const wo = (group.woNumber || '').trim();
                                return wo ? `${wo}-${rIdx + 1}` : `${rIdx + 1}`;
                              })()}
                              onUpdate={(f, v) => updateGlassRow(group.id, row.id, f, v)}
                              onRemove={() => removeGlassRow(group.id, row.id)}
                            />
                          ))}
                          <button
                            onClick={() => addGlassRow(group.id)}
                            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px dashed rgba(180,80,0,0.45)', background: 'transparent', color: '#b85000', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                          >+ Add Glass Row</button>
                        </div>
                      ))}
                      <button
                        onClick={addGlassGroup}
                        style={{ padding: '9px 18px', borderRadius: '8px', border: '1px dashed rgba(180,80,0,0.6)', background: 'rgba(180,80,0,0.04)', color: '#b85000', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginTop: '4px' }}
                      >+ Add Another Customer Group</button>
                    </div>
                  )}

                  {/* ── SCREEN TAB ────────────────────────────────────────────── */}
                  {repairTab === 'screen' && (
                    <div>
                      {screenGroups.map((group, gIdx) => (
                        <div key={group.id} style={{
                          background: 'rgba(255,237,213,0.35)',
                          border: '1px solid rgba(180,80,0,0.25)',
                          borderRadius: '12px',
                          padding: '12px 14px',
                          marginBottom: '12px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <div style={{
                              fontSize: '10px', fontWeight: '800',
                              color: 'white', background: '#b85000',
                              padding: '3px 9px', borderRadius: '999px',
                              letterSpacing: '0.05em',
                            }}>CUSTOMER {gIdx + 1}</div>
                            <div style={{ fontSize: '10px', color: '#9a7a60', fontStyle: 'italic' }}>
                              {group.rows.filter(r => r.width && r.height).length} / {group.rows.length} row{group.rows.length !== 1 ? 's' : ''} filled
                            </div>
                          </div>
                          <GroupHeader
                            group={group}
                            onUpdate={(f, v) => updateScreenGroup(group.id, f, v)}
                            onRemoveGroup={() => removeScreenGroup(group.id)}
                            canRemove={screenGroups.length > 1}
                            rowCount={group.rows.length}
                          />
                          {group.rows.map((row, rIdx) => (
                            <ScreenRepairRow
                              key={row.id}
                              row={row}
                              rowIndex={rIdx}
                              autoLabel={(() => {
                                const wo = (group.woNumber || '').trim();
                                return wo ? `${wo}-${rIdx + 1}` : `${rIdx + 1}`;
                              })()}
                              onUpdate={(f, v) => updateScreenRow(group.id, row.id, f, v)}
                              onRemove={() => removeScreenRow(group.id, row.id)}
                            />
                          ))}
                          <button
                            onClick={() => addScreenRow(group.id)}
                            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px dashed rgba(180,80,0,0.45)', background: 'transparent', color: '#b85000', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                          >+ Add Screen Row</button>
                        </div>
                      ))}
                      <button
                        onClick={addScreenGroup}
                        style={{ padding: '9px 18px', borderRadius: '8px', border: '1px dashed rgba(180,80,0,0.6)', background: 'rgba(180,80,0,0.04)', color: '#b85000', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginTop: '4px' }}
                      >+ Add Another Customer Group</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── ERROR ─────────────────────────────────────────────────────────── */}
        {error && (
          <div style={{ background: 'rgba(220,50,50,0.06)', border: '1px solid rgba(220,50,50,0.18)', borderRadius: '12px', padding: '16px 20px', color: '#cc3333', fontSize: '13px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div><div style={{ fontWeight: '700', marginBottom: '2px' }}>Connection error</div><div style={{ opacity: 0.8 }}>{error}</div></div>
            <button onClick={loadJobs} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: '8px', border: 'none', background: 'rgba(220,50,50,0.1)', color: '#cc3333', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit' }}>Retry</button>
          </div>
        )}

        {/* ── LOADING ───────────────────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '80px 40px' }}>
            <div style={{ width: '40px', height: '40px', border: `3px solid rgba(139,69,19,0.15)`, borderTopColor: WALNUT, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ fontSize: '14px', color: '#bbb', fontWeight: '500' }}>Loading jobs...</div>
          </div>
        )}

        {/* ── JOB LISTS ─────────────────────────────────────────────────────── */}
        {!loading && (() => {
          const activeJobs   = filtered.filter(j =>  isActiveJob(j));
          const archivedJobs = filtered.filter(j => !isActiveJob(j));
          const allWeekGroups = groupJobsByWeek(activeJobs);
          const now           = new Date();

          const totalPages  = Math.max(1, Math.ceil(allWeekGroups.length / WEEKS_PER_PAGE));
          const safePage    = Math.min(currentPage, totalPages);
          const pageStart   = (safePage - 1) * WEEKS_PER_PAGE;
          const weekGroups  = allWeekGroups.slice(pageStart, pageStart + WEEKS_PER_PAGE);

          return (
            <>
              {activeJobs.length === 0 && (
                <div style={{ textAlign: 'center', padding: '52px 40px', background: PAPER, borderRadius: '16px', border: '1px solid rgba(139,69,19,0.08)', marginBottom: '28px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: INK, marginBottom: '5px' }}>{searchQuery ? 'No current jobs match your search' : 'No current jobs'}</div>
                  <div style={{ fontSize: '12px', color: STONE, marginBottom: '18px' }}>{searchQuery ? 'Try a different search term' : 'Jobs created in the last 4 weeks show here'}</div>
                  {!searchQuery && <Button size="small" onClick={() => navigate('/cutlist/new')}>+ Create New Job</Button>}
                </div>
              )}

              {weekGroups.map((group) => {
                const label      = getWeekLabel(group.weekStart, now);
                const range      = formatWeekRange(group.weekStart);
                const isThisWeek = label === 'This Week';
                const isLastWeek = label === 'Last Week';
                const dotColor   = isThisWeek ? MOSS : isLastWeek ? WALNUT : STONE;
                const labelColor = isThisWeek ? MOSS : isLastWeek ? WALNUT : STONE;

                return (
                  <div key={group.weekStart.toISOString()} style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: dotColor, boxShadow: `0 0 0 3px ${dotColor}33`, flexShrink: 0 }} />
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '800', color: labelColor, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
                        <span style={{ fontSize: '12px', color: '#bbb', fontWeight: '500' }}>{label === 'Week of' ? range : `— ${range}`}</span>
                      </div>
                      <div style={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${dotColor}33 0%, transparent 100%)` }} />
                      <span style={{ fontSize: '11px', color: '#aaa', fontWeight: '500' }}>{group.jobs.length} job{group.jobs.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
                      {group.jobs.map(job => (
                        <JobRow key={job.id} job={job} dimmed={false} deletingId={deletingId} onDelete={handleDelete} />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '32px', marginTop: '8px' }}>
                  <button
                    onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={safePage === 1}
                    style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid rgba(139,69,19,0.2)', background: safePage === 1 ? '#f5f5f5' : 'white', color: safePage === 1 ? '#ccc' : WALNUT, fontSize: '13px', fontWeight: '600', cursor: safePage === 1 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
                  >← Newer</button>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        style={{ width: '32px', height: '32px', borderRadius: '8px', border: 'none', background: p === safePage ? `linear-gradient(135deg, ${WALNUT} 0%, ${WALNUT_LT} 100%)` : 'rgba(139,69,19,0.07)', color: p === safePage ? 'white' : WALNUT, fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', boxShadow: p === safePage ? '0 2px 8px rgba(139,69,19,0.3)' : 'none' }}
                      >{p}</button>
                    ))}
                  </div>
                  <button
                    onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={safePage === totalPages}
                    style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid rgba(139,69,19,0.2)', background: safePage === totalPages ? '#f5f5f5' : 'white', color: safePage === totalPages ? '#ccc' : WALNUT, fontSize: '13px', fontWeight: '600', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
                  >Older →</button>
                </div>
              )}

              {/* Past jobs (archived) */}
              {archivedJobs.length > 0 && (
                <>
                  <div onClick={() => setShowArchived(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '16px', userSelect: 'none' }}>
                    <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(0,0,0,0.09) 0%, transparent 100%)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 16px', borderRadius: '999px', background: showArchived ? 'rgba(0,0,0,0.07)' : 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.09)', fontSize: '11px', fontWeight: '700', color: '#777', textTransform: 'uppercase', letterSpacing: '0.07em', transition: 'all 0.15s' }}>
                      Past Jobs — {archivedJobs.length} job{archivedJobs.length !== 1 ? 's' : ''}
                      <span style={{ display: 'inline-block', transition: 'transform 0.25s', transform: showArchived ? 'rotate(180deg)' : 'rotate(0deg)', fontSize: '11px' }}>v</span>
                    </div>
                    <div style={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.09) 100%)' }} />
                  </div>
                  {/* SEARCH FIX: archived matches auto-show when searching */}
                  {(showArchived || searchQuery.trim()) && (
                    <>
                      <div style={{ fontSize: '11px', color: '#bbb', marginBottom: '14px', fontStyle: 'italic', paddingLeft: '2px' }}>Jobs older than 4 weeks — still fully accessible</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
                        {archivedJobs.map(job => (
                          <JobRow key={job.id} job={job} dimmed={true} deletingId={deletingId} onDelete={handleDelete} />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          );
        })()}
      </div>

      {/* GLASS ORDER VERIFICATION CHECKPOINT */}
      {glassVerify && (() => {
        const { summary, verify } = glassVerify;
        const ok = verify.match && summary.allOk;
        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setGlassVerify(null); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 9000,
              background: 'rgba(20,14,8,0.32)', backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '24px', boxSizing: 'border-box',
            }}
          >
            <div style={{
              background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px',
              maxHeight: '88vh', overflowY: 'auto',
              boxShadow: '0 24px 70px rgba(0,0,0,0.34)', fontFamily: 'inherit',
            }}>
              <div style={{ padding: '22px 24px 14px' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: '#8B4513', marginBottom: '4px' }}>
                  The Window Place LLC — Glass Order
                </div>
                <div style={{ fontSize: '19px', fontWeight: 800, color: '#1a0a00' }}>
                  Verify Before Export
                </div>
                <div style={{ fontSize: '12px', color: '#6b5d4f', marginTop: '3px', lineHeight: 1.5 }}>
                  Review each job's glass count, then confirm. Please recheck the numbers
                  even when they match.
                </div>
              </div>

              <div style={{
                margin: '0 24px 14px', padding: '11px 14px', borderRadius: '10px',
                background: ok ? '#e9f6ec' : '#fdecec',
                border: '1px solid ' + (ok ? '#9ccfa6' : '#e6a5a5'),
                fontSize: '12.5px', fontWeight: 700,
                color: ok ? '#1a5a2c' : '#9a2020',
              }}>
                {ok
                  ? '\u2713 PDF matches the database. Counts verified — still recheck below.'
                  : '\u2717 MISMATCH — the PDF does not match the database. Recheck the calculation before sending.'}
                <div style={{ fontWeight: 500, marginTop: '4px', color: ok ? '#3a6a47' : '#7a3030' }}>
                  Database expects {verify.dbExpected} glass pieces &middot; PDF order has {verify.pdfTotal}.
                </div>
              </div>

              <div style={{ padding: '0 24px' }}>
                {summary.jobs.map((j, i) => (
                  <div key={i} style={{
                    border: '1px solid #ece6dd', borderRadius: '11px',
                    padding: '12px 14px', marginBottom: '9px',
                    background: j.ok ? '#fff' : '#fdf3f3',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'baseline', marginBottom: '6px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#1a0a00' }}>
                        WO# {j.woNumber}
                        <span style={{ fontWeight: 500, color: '#8a7a6a', marginLeft: '7px' }}>
                          {j.customerName}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 800,
                        color: j.ok ? '#1a5a2c' : '#9a2020' }}>
                        {j.ok ? '\u2713 ' : '\u2717 '}{j.expectedGlass} glass
                      </div>
                    </div>
                    {j.lines.map((ln, k) => (
                      <div key={k} style={{ fontSize: '12px', color: '#5a4d40',
                        display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                        <span>{ln.windows} &times; {ln.label}</span>
                        <span style={{ fontWeight: 600 }}>{ln.expectedGlass} glass</span>
                      </div>
                    ))}
                    {!j.ok && (
                      <div style={{ fontSize: '11px', color: '#9a2020', fontWeight: 700,
                        marginTop: '5px' }}>
                        Calc produced {j.actualGlass} — expected {j.expectedGlass}.
                      </div>
                    )}
                  </div>
                ))}

                {summary.unknownTypes.length > 0 && (
                  <div style={{ fontSize: '11.5px', color: '#9a2020', fontWeight: 700,
                    padding: '8px 0' }}>
                    Unknown window type(s): {summary.unknownTypes.join(', ')} — count cannot be verified.
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '12px 14px', marginTop: '4px',
                  background: '#1a0a00', borderRadius: '11px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#e8dccb',
                    textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Total Glass Pieces
                  </span>
                  <span style={{ fontFamily: 'Georgia, serif', fontSize: '22px',
                    fontWeight: 700, color: '#fff' }}>
                    {summary.totalExpected}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', padding: '16px 24px 20px' }}>
                <button
                  onClick={() => setGlassVerify(null)}
                  style={{ flex: 1, padding: '12px', borderRadius: '9px',
                    border: '1.5px solid #ddd', background: '#fff', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 700, color: '#666', fontFamily: 'inherit' }}>
                  Cancel
                </button>
                <button
                  onClick={confirmGlassExport}
                  style={{ flex: 2, padding: '12px', borderRadius: '9px', border: 'none',
                    background: '#8B4513', color: '#fff', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 800, fontFamily: 'inherit',
                    boxShadow: '0 4px 16px rgba(139,69,19,0.3)' }}>
                  Confirm &amp; Export PDF
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </AppLayout>
  );
}